import { useState, useCallback, useEffect } from "react";
import { mutate } from "swr";
import fetch from "cross-fetch";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import useCurrentAccount from "@/components/context/useCurrentAccount";
import useTeamMembers, {
  TeamMember,
  TeamRole,
  getRoleLabel,
  isAdmin,
} from "@/components/context/useTeamMembers";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { PageHeader } from "@/components/ui/page-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { TrashIcon, UserPlusIcon, PencilIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { Alert, AlertDescription } from "@/components/ui/Alert";
import { formatDistanceToNow } from "date-fns";

function getRoleBadgeVariant(role: TeamRole): "default" | "secondary" | "outline" {
  switch (role) {
    case "admin":
      return "default";
    case "editor":
      return "secondary";
    case "viewer":
      return "outline";
  }
}

interface InviteMemberFormProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

function InviteMemberForm({ open, setOpen }: InviteMemberFormProps) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<TeamRole>("viewer");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setEmail("");
      setRole("viewer");
      setError(null);
    }
  }, [open]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!email.trim() || loading) return;

      setLoading(true);
      setError(null);

      try {
        const response = await fetch("/web_api/team/invite", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: email.trim(), role }),
        });
        const data = await response.json();

        if (data.status === "ok") {
          mutate("/web_api/team/members");
          mutate("/web_api/accounts/current");
          setEmail("");
          setRole("viewer");
          setOpen(false);
        } else {
          setError(data.error || "Failed to send invite");
        }
      } catch {
        setError("Failed to send invite");
      } finally {
        setLoading(false);
      }
    },
    [email, role, loading, setOpen],
  );

  return (
    <Dialog open={open} onClose={() => setOpen(false)}>
      <DialogHeader onClose={() => setOpen(false)}>
        <DialogTitle>Invite Team Member</DialogTitle>
        <DialogDescription>
          Send an invitation to join your team. They will receive an email with a link to accept.
        </DialogDescription>
      </DialogHeader>
      <DialogBody>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium mb-1">
              Email Address
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="colleague@company.com"
              className="w-full px-3 py-2 border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              required
            />
          </div>
          <div>
            <label htmlFor="role" className="block text-sm font-medium mb-1">
              Role
            </label>
            <select
              id="role"
              value={role}
              onChange={(e) => setRole(e.target.value as TeamRole)}
              className="w-full px-3 py-2 border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring cursor-pointer"
            >
              <option value="viewer">Viewer - Read-only access</option>
              <option value="editor">Editor - Can create and edit resources</option>
              <option value="admin">
                Admin - Full access including billing and team management
              </option>
            </select>
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </form>
      </DialogBody>
      <DialogFooter>
        <Button variant="outline" onClick={() => setOpen(false)}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} loading={loading} disabled={!email.trim()}>
          Send Invite
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

interface ChangeRoleDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  member: TeamMember | null;
}

function ChangeRoleDialog({ open, setOpen, member }: ChangeRoleDialogProps) {
  const [role, setRole] = useState<TeamRole>(member?.role || "viewer");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (member) {
      setRole(member.role);
    }
  }, [member]);

  const handleSubmit = useCallback(async () => {
    if (!member || loading) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`/web_api/team/members/${member.id}/role`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ role }),
      });
      const data = await response.json();

      if (data.status === "ok") {
        mutate("/web_api/team/members");
        setOpen(false);
      } else {
        setError(data.error || "Failed to change role");
      }
    } catch {
      setError("Failed to change role");
    } finally {
      setLoading(false);
    }
  }, [member, role, loading, setOpen]);

  return (
    <Dialog open={open} onClose={() => setOpen(false)}>
      <DialogHeader onClose={() => setOpen(false)}>
        <DialogTitle>Change Role</DialogTitle>
        <DialogDescription>Change the role for {member?.email}</DialogDescription>
      </DialogHeader>
      <DialogBody>
        <div>
          <label htmlFor="role-change" className="block text-sm font-medium mb-1">
            Role
          </label>
          <select
            id="role-change"
            value={role}
            onChange={(e) => setRole(e.target.value as TeamRole)}
            className="w-full px-3 py-2 border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring cursor-pointer"
          >
            <option value="viewer">Viewer - Read-only access</option>
            <option value="editor">Editor - Can create and edit resources</option>
            <option value="admin">Admin - Full access including billing and team management</option>
          </select>
        </div>
        {error && <p className="text-sm text-destructive mt-2">{error}</p>}
      </DialogBody>
      <DialogFooter>
        <Button variant="outline" onClick={() => setOpen(false)}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} loading={loading}>
          Save Changes
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

interface TeamMemberRowProps {
  member: TeamMember;
  isAdmin: boolean;
  currentUserEmail: string;
  onChangeRole: (member: TeamMember) => void;
  onRemove: (member: TeamMember) => void;
}

function TeamMemberRow({
  member,
  isAdmin,
  currentUserEmail,
  onChangeRole,
  onRemove,
}: TeamMemberRowProps) {
  const isCurrentUser = member.email === currentUserEmail;

  return (
    <TableRow>
      <TableCell className="font-medium">
        {member.email}
        {isCurrentUser && <span className="ml-2 text-xs text-muted-foreground">(you)</span>}
      </TableCell>
      <TableCell>
        <Badge variant={getRoleBadgeVariant(member.role)}>{getRoleLabel(member.role)}</Badge>
      </TableCell>
      <TableCell>
        {member.is_pending ? (
          <Badge variant="warning">Pending</Badge>
        ) : (
          <span className="text-muted-foreground">
            {formatDistanceToNow(new Date(member.created_at * 1000), { addSuffix: true })}
          </span>
        )}
      </TableCell>
      <TableCell className="text-right">
        {isAdmin && !isCurrentUser && (
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onChangeRole(member)}
              title="Change role"
            >
              <PencilIcon className="h-4 w-4" />
              <span className="sr-only">Change role for {member.email}</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onRemove(member)}
              className="text-destructive hover:text-destructive"
              title={member.is_pending ? "Cancel invite" : "Remove member"}
            >
              <TrashIcon className="h-4 w-4" />
              <span className="sr-only">
                {member.is_pending ? "Cancel invite for" : "Remove"} {member.email}
              </span>
            </Button>
          </div>
        )}
      </TableCell>
    </TableRow>
  );
}

export default function TeamPage() {
  const { account, team, isLoading: accountLoading } = useCurrentAccount({ requireLoggedIn: true });
  const { members, isLoading: membersLoading } = useTeamMembers();
  const [inviteFormOpen, setInviteFormOpen] = useState(false);
  const [changeRoleDialogOpen, setChangeRoleDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const isTeamAdmin = isAdmin(team?.role);
  const isLoading = accountLoading || membersLoading;

  const handleRemove = useCallback(async (member: TeamMember) => {
    const action = member.is_pending ? "cancel this invite" : "remove this team member";
    if (!confirm(`Are you sure you want to ${action}?`)) return;

    setRemoveError(null);

    const endpoint = member.is_pending
      ? `/web_api/team/invite/${member.id}`
      : `/web_api/team/members/${member.id}`;

    try {
      const response = await fetch(endpoint, { method: "DELETE" });
      const data = await response.json();

      if (data.status === "ok") {
        mutate("/web_api/team/members");
        mutate("/web_api/accounts/current");
      } else {
        setRemoveError(data.error || "Failed to remove member");
      }
    } catch {
      setRemoveError("Failed to remove member. Please try again.");
    }
  }, []);

  const handleChangeRole = useCallback((member: TeamMember) => {
    setSelectedMember(member);
    setChangeRoleDialogOpen(true);
  }, []);

  return (
    <DashboardLayout activePath="/dashboard/team" title="Team" breadcrumbs={[{ label: "Team" }]}>
      <div className="mx-auto max-w-6xl px-4 py-8">
        <PageHeader
          title="Team"
          description="Manage your team members and their access permissions"
        >
          {isTeamAdmin && (
            <Button onClick={() => setInviteFormOpen(true)}>
              <UserPlusIcon className="h-4 w-4 mr-2" />
              Invite Member
            </Button>
          )}
        </PageHeader>

        <div className="space-y-6">
          {/* Error Alert */}
          {removeError && (
            <Alert variant="destructive" className="flex items-center justify-between">
              <AlertDescription>{removeError}</AlertDescription>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setRemoveError(null)}
                className="h-6 w-6 p-0"
              >
                <XMarkIcon className="h-4 w-4" />
                <span className="sr-only">Dismiss</span>
              </Button>
            </Alert>
          )}

          {/* Seat Usage Card */}
          <Card>
            <CardHeader>
              <CardTitle>Seat Usage</CardTitle>
              <CardDescription>Unlimited seats</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="animate-pulse h-4 w-32 bg-muted rounded" />
              ) : (
                <span className="text-sm font-medium">{team?.seat_count || 1} seats used</span>
              )}
            </CardContent>
          </Card>

          {/* Team Members Card */}
          <Card>
            <CardHeader>
              <CardTitle>Team Members</CardTitle>
              <CardDescription>People with access to this account</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="animate-pulse h-12 bg-muted rounded" />
                  ))}
                </div>
              ) : members.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p>No team members yet.</p>
                </div>
              ) : (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Email</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {members.map((member) => (
                        <TeamMemberRow
                          key={member.id}
                          member={member}
                          isAdmin={isTeamAdmin}
                          currentUserEmail={account?.email || ""}
                          onChangeRole={handleChangeRole}
                          onRemove={handleRemove}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Permissions Info Card */}
          <Card>
            <CardHeader>
              <CardTitle>Role Permissions</CardTitle>
              <CardDescription>What each role can do</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <h4 className="font-medium flex items-center gap-2">
                    <Badge variant="outline">Viewer</Badge>
                  </h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>View monitors and their status</li>
                    <li>View contacts and status pages</li>
                    <li>View incidents and notifications</li>
                  </ul>
                </div>
                <div className="space-y-2">
                  <h4 className="font-medium flex items-center gap-2">
                    <Badge variant="secondary">Editor</Badge>
                  </h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>Everything Viewers can do</li>
                    <li>Create and edit monitors</li>
                    <li>Create and edit contacts</li>
                    <li>Create and edit status pages</li>
                    <li>Manage API keys</li>
                  </ul>
                </div>
                <div className="space-y-2">
                  <h4 className="font-medium flex items-center gap-2">
                    <Badge>Admin</Badge>
                  </h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>Everything Editors can do</li>
                    <li>Manage team members</li>
                    <li>Manage billing and subscription</li>
                    <li>Delete the account</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <InviteMemberForm open={inviteFormOpen} setOpen={setInviteFormOpen} />

        <ChangeRoleDialog
          open={changeRoleDialogOpen}
          setOpen={setChangeRoleDialogOpen}
          member={selectedMember}
        />
      </div>
    </DashboardLayout>
  );
}
